import { useTranslation } from 'react-i18next';
import type { MessageResponse } from '@/api';
import {
  firstLine,
  isNostr,
  type MessageData,
  parseMessageData,
  senderFromData,
} from '@/lib/messages';
import { listTime } from '@/lib/relative-time';
import classes from './Inbox.module.css';
import { VerdictTag } from './VerdictTag';

export interface MessageRowProps {
  message: MessageResponse;
  formTitle?: string;
  files: number;
  decrypted?: MessageData;
  selected: boolean;
  checked: boolean;
  onOpen: () => void;
  /** `range` is a shift-click: extend from the last row checked. */
  onCheck: (checked: boolean, range: boolean) => void;
}

function PaperclipIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m21 11-8.5 8.5a5 5 0 0 1-7-7L14 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L15 7" />
    </svg>
  );
}

export function MessageRow({
  message,
  formTitle,
  files,
  decrypted,
  selected,
  checked,
  onOpen,
  onCheck,
}: MessageRowProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.split('-')[0];
  const nostr = isNostr(message);
  const data = nostr ? decrypted : parseMessageData(message);
  const fromData = data ? senderFromData(data) : {};
  const sender =
    message.sender_name ||
    message.sender_email ||
    fromData.name ||
    fromData.email ||
    (nostr && !data ? t('inbox.encryptedSender') : t('inbox.noSender'));
  const preview = data ? firstLine(data) : nostr ? t('inbox.encryptedFirstLine') : '';
  // Spam stays out of the unread count, so it carries no unread marker.
  const unread = !message.is_read && !message.is_spam && !selected;

  return (
    <div className={selected ? `${classes.row} ${classes.selected}` : classes.row}>
      <span className={classes.check}>
        <input
          type="checkbox"
          className={classes.checkbox}
          checked={checked}
          onChange={(e) =>
            onCheck(e.currentTarget.checked, (e.nativeEvent as MouseEvent).shiftKey === true)
          }
          aria-label={t('inbox.select', { sender })}
        />
      </span>
      <button
        type="button"
        className={classes.open}
        onClick={onOpen}
        aria-current={selected ? 'true' : undefined}
      >
        <span>
          {unread && <span className={classes.dot} role="img" aria-label={t('inbox.unread')} />}
        </span>
        <span className={classes.middle}>
          <span className={classes.senderLine}>
            <span className={unread ? `${classes.sender} ${classes.unreadSender}` : classes.sender}>
              {sender}
            </span>
            <span className={`${classes.small} ${classes.ellipsis}`}>{formTitle ?? ''}</span>
          </span>
          <span className={`${classes.sub} ${classes.ellipsis}`}>{preview}</span>
        </span>
        <span className={classes.right}>
          <span className={`${classes.small} ${classes.ellipsis}`}>
            {listTime(message.created_at, locale)}
          </span>
          <span className={classes.tags}>
            {files > 0 && (
              <span className={classes.files}>
                <PaperclipIcon />
                <span aria-hidden="true">{files}</span>
                <span className={classes.srOnly}>{t('inbox.files', { count: files })}</span>
              </span>
            )}
            <VerdictTag message={message} />
          </span>
        </span>
      </button>
    </div>
  );
}
